import { useEffect, useMemo, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Table from '@mui/joy/Table'
import IconButton from '@mui/joy/IconButton'
import Tooltip from '@mui/joy/Tooltip'
import CircularProgress from '@mui/joy/CircularProgress'
import Alert from '@mui/joy/Alert'
import Link from '@mui/joy/Link'
import Button from '@mui/joy/Button'
import Checkbox from '@mui/joy/Checkbox'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VideocamIcon from '@mui/icons-material/Videocam'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import { AttendeeChip } from './AttendeeChip'
import { useUpcomingCalls } from '../../hooks/useUpcomingCalls'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'
import { adminCalendars, deleteCalendarEvent, getWorkspaceConfig, timestampToDate, type CalendarSkipReason } from '../../api/dsGmeetApi'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

function fmtLocal(d: Date | null): string {
    if (!d) return '—'
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function durationMin(start: Date | null, end: Date | null): string {
    if (!start || !end) return ''
    const min = Math.round((end.getTime() - start.getTime()) / 60_000)
    return min > 0 ? `${min} min` : ''
}

const SKIP_LABEL: Record<CalendarSkipReason, string> = {
    NO_VIDEO_URL: 'No video URL',
    PAST: 'Already past',
    NOT_SALES: 'Not sales role',
    OPT_OUT: 'Opted out',
    NO_EXTERNAL: 'No external attendees',
    MANUAL_OVERRIDE: 'Manually skipped',
}

const SKIP_HINT: Record<CalendarSkipReason, string> = {
    NO_VIDEO_URL: 'No Google Meet / Zoom / Teams link found. Add a video meeting link to enable auto-recording.',
    PAST: 'Event start time has already passed.',
    NOT_SALES: "The calendar owner's role is not Sales or Sales Lead.",
    OPT_OUT: 'Title contains an opt-out marker (e.g. [no-record] or numeo:skip).',
    NO_EXTERNAL: 'All attendees are from internal email domains. At least one external attendee is required.',
    MANUAL_OVERRIDE: 'This event was manually excluded from recording.',
}

function meetingPlatform(url: string | null): string {
    if (!url) return 'video link'
    if (url.includes('meet.google.com')) return 'Google Meet'
    if (url.includes('zoom.us')) return 'Zoom'
    if (url.includes('teams.microsoft.com') || url.includes('teams.live.com')) return 'Teams'
    if (url.includes('webex.com')) return 'Webex'
    return 'video link'
}

export function UpcomingTab() {
    const userId = useCurrentUserId()
    const { items, loading, error, refetch } = useUpcomingCalls(userId)
    const [internalDomains, setInternalDomains] = useState<string[]>([])
    const [calendarOwner, setCalendarOwner] = useState<Record<string, string>>({})
    const [deleting, setDeleting] = useState<Set<string>>(new Set())
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState<number>(20)

    useEffect(() => {
        getWorkspaceConfig()
            .then((cfg) => setInternalDomains(cfg.internalEmailDomains))
            .catch(() => undefined)
    }, [])

    useEffect(() => {
        adminCalendars()
            .then((cals) => setCalendarOwner(Object.fromEntries(cals.map((c) => [c.recall_calendar_id, c.account_email]))))
            .catch(() => undefined)
    }, [])

    // Deduplicate by ical_uid (same meeting on multiple connected calendars), then sort by start_time asc.
    const dedupedItems = useMemo(() => {
        const seen = new Set<string>()
        const sorted = [...items].sort((a, b) => {
            const ta = a.start_time ? a.start_time._seconds : 0
            const tb = b.start_time ? b.start_time._seconds : 0
            return ta - tb
        })
        return sorted.filter((ev) => {
            if (!ev.ical_uid) return true
            if (seen.has(ev.ical_uid)) return false
            seen.add(ev.ical_uid)
            return true
        })
    }, [items])

    // Reset to first page when items or page size change
    useEffect(() => {
        setPage(0)
    }, [dedupedItems.length, pageSize])

    // Clear selections that no longer exist
    useEffect(() => {
        const ids = new Set(dedupedItems.map((e) => e.recall_calendar_event_id))
        setSelected((s) => new Set([...s].filter((id) => ids.has(id))))
    }, [dedupedItems])

    const totalPages = Math.max(1, Math.ceil(dedupedItems.length / pageSize))
    const pageItems = dedupedItems.slice(page * pageSize, (page + 1) * pageSize)

    const allPageIds = pageItems.map((e) => e.recall_calendar_event_id)
    const allPageSelected = allPageIds.length > 0 && allPageIds.every((id) => selected.has(id))
    const someSelected = selected.size > 0

    const toggleAll = () => {
        if (allPageSelected)
            setSelected((s) => {
                const n = new Set(s)
                allPageIds.forEach((id) => n.delete(id))
                return n
            })
        else setSelected((s) => new Set([...s, ...allPageIds]))
    }

    const toggleOne = (id: string) => {
        setSelected((s) => {
            const n = new Set(s)
            if (n.has(id)) n.delete(id)
            else n.add(id)
            return n
        })
    }

    // Expand a set of event IDs to include hidden duplicates sharing the same ical_uid.
    const expandToAllDuplicates = (ids: string[]): string[] => {
        const uids = new Set(ids.map((id) => items.find((e) => e.recall_calendar_event_id === id)?.ical_uid).filter(Boolean) as string[])
        const all = new Set(ids)
        items.forEach((e) => {
            if (e.ical_uid && uids.has(e.ical_uid)) all.add(e.recall_calendar_event_id)
        })
        return [...all]
    }

    const handleDelete = async (eventId: string) => {
        const ids = expandToAllDuplicates([eventId])
        ids.forEach((id) => setDeleting((s) => new Set(s).add(id)))
        try {
            await Promise.allSettled(ids.map((id) => deleteCalendarEvent(id)))
            await refetch()
        } catch {
            // silent — row stays
        } finally {
            setDeleting((s) => {
                const n = new Set(s)
                ids.forEach((id) => n.delete(id))
                return n
            })
        }
    }

    const handleBulkDelete = async () => {
        const ids = expandToAllDuplicates([...selected])
        setBulkDeleting(true)
        setDeleting(new Set(ids))
        await Promise.allSettled(ids.map((id) => deleteCalendarEvent(id)))
        setSelected(new Set())
        setDeleting(new Set())
        setBulkDeleting(false)
        await refetch()
    }

    return (
        <Stack gap={1.5} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack gap={0.25}>
                    <Typography level="title-sm">Upcoming meetings</Typography>
                    <Typography level="body-xs" color="neutral">
                        Next 14 days · green rows will be auto-recorded
                    </Typography>
                </Stack>
                <Stack direction="row" gap={0.75} alignItems="center">
                    {someSelected && (
                        <Button
                            size="sm"
                            color="danger"
                            variant="soft"
                            startDecorator={bulkDeleting ? <CircularProgress size="sm" /> : <DeleteOutlineIcon />}
                            disabled={bulkDeleting}
                            onClick={() => void handleBulkDelete()}
                        >
                            Delete {selected.size}
                        </Button>
                    )}
                    {/* Page size selector */}
                    <Select size="sm" value={pageSize} onChange={(_, v) => v && setPageSize(v)} sx={{ minWidth: 70, fontSize: 12 }}>
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <Option key={n} value={n}>
                                {n} / page
                            </Option>
                        ))}
                    </Select>
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Stack direction="row" alignItems="center" gap={0.25}>
                            <IconButton size="sm" variant="plain" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography level="body-xs" color="neutral" sx={{ minWidth: 52, textAlign: 'center' }}>
                                {page + 1} / {totalPages}
                            </Typography>
                            <IconButton size="sm" variant="plain" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    )}
                    <Tooltip title="Refresh">
                        <IconButton size="sm" variant="plain" onClick={refetch} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && <Alert color="danger">{error}</Alert>}

            {/* Scrollable table — grows to fill remaining vertical space */}
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', flex: 1, minHeight: 0 }}>
                <Table stickyHeader hoverRow size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: 40, textAlign: 'center' }}>
                                <Checkbox
                                    size="sm"
                                    checked={allPageSelected}
                                    indeterminate={someSelected && !allPageSelected}
                                    onChange={toggleAll}
                                    disabled={pageItems.length === 0}
                                />
                            </th>
                            <th style={{ width: 28 }} />
                            <th>Title</th>
                            <th style={{ width: 190 }}>Start · Duration</th>
                            <th style={{ width: 170 }}>Recording</th>
                            <th>Attendees</th>
                            <th style={{ width: 36 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((ev) => {
                            const start = timestampToDate(ev.start_time)
                            const end = timestampToDate(ev.end_time)
                            const externals = ev.attendees.filter((a) => {
                                const domain = a.email.split('@')[1]?.toLowerCase() ?? ''
                                return domain && !internalDomains.includes(domain)
                            })
                            const isSelected = selected.has(ev.recall_calendar_event_id)

                            // Color coding: selected > will-record (green) > will-skip (no bg)
                            const rowBg = isSelected ? 'var(--joy-palette-primary-softBg)' : ev.qualifies ? 'var(--joy-palette-success-softBg)' : undefined

                            return (
                                <tr key={ev.recall_calendar_event_id} style={{ background: rowBg }}>
                                    <td style={{ textAlign: 'center' }}>
                                        <Checkbox size="sm" checked={isSelected} onChange={() => toggleOne(ev.recall_calendar_event_id)} />
                                    </td>

                                    <td>
                                        <Tooltip title={ev.qualifies ? 'Will be recorded' : 'Will not be recorded'}>
                                            <FiberManualRecordIcon
                                                fontSize="small"
                                                sx={{ color: ev.qualifies ? 'success.500' : 'neutral.300', display: 'block' }}
                                            />
                                        </Tooltip>
                                    </td>

                                    <td>
                                        <Stack gap={0.25}>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <Typography level="body-sm" fontWeight="md">
                                                    {ev.title ?? '(untitled)'}
                                                </Typography>
                                                {ev.meeting_url && (
                                                    <Tooltip title={ev.meeting_url}>
                                                        <Link href={ev.meeting_url} target="_blank" rel="noopener" level="body-xs">
                                                            <VideocamIcon fontSize="small" />
                                                        </Link>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            {calendarOwner[ev.recall_calendar_id] && (
                                                <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                                                    {calendarOwner[ev.recall_calendar_id]}
                                                </Typography>
                                            )}
                                            {ev.scheduled_recall_bot_id && (
                                                <Typography level="body-xs" color="neutral" fontFamily="monospace">
                                                    bot {ev.scheduled_recall_bot_id.slice(0, 8)}…
                                                </Typography>
                                            )}
                                        </Stack>
                                    </td>

                                    <td>
                                        <Stack gap={0.15}>
                                            <Typography level="body-xs">{fmtLocal(start)}</Typography>
                                            {durationMin(start, end) && (
                                                <Typography level="body-xs" color="neutral">
                                                    {durationMin(start, end)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </td>

                                    <td>
                                        {ev.qualifies ? (
                                            <Tooltip
                                                title={`${meetingPlatform(ev.meeting_url)} · ${externals.length} external attendee${externals.length !== 1 ? 's' : ''}`}
                                                placement="top"
                                            >
                                                <Chip size="sm" variant="soft" color="success">
                                                    Will record
                                                </Chip>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={ev.skip_reason ? (SKIP_HINT[ev.skip_reason] ?? ev.skip_reason) : ''} placement="top">
                                                <Chip size="sm" variant="soft" color="neutral">
                                                    Skip: {ev.skip_reason ? (SKIP_LABEL[ev.skip_reason] ?? ev.skip_reason) : '—'}
                                                </Chip>
                                            </Tooltip>
                                        )}
                                    </td>

                                    <td>
                                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                                            {externals.slice(0, 3).map((a) => (
                                                <AttendeeChip key={a.email} email={a.email} name={a.name} />
                                            ))}
                                            {externals.length > 3 && (
                                                <Chip size="sm" variant="soft">
                                                    +{externals.length - 3}
                                                </Chip>
                                            )}
                                            {externals.length === 0 && (
                                                <Typography level="body-xs" color="neutral">
                                                    (internal only)
                                                </Typography>
                                            )}
                                        </Stack>
                                    </td>

                                    <td>
                                        <Tooltip title="Remove from list">
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                                disabled={deleting.has(ev.recall_calendar_event_id)}
                                                onClick={() => void handleDelete(ev.recall_calendar_event_id)}
                                            >
                                                {deleting.has(ev.recall_calendar_event_id) ? (
                                                    <CircularProgress size="sm" />
                                                ) : (
                                                    <DeleteOutlineIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
                {loading && (
                    <Stack alignItems="center" sx={{ p: 2 }}>
                        <CircularProgress size="sm" />
                    </Stack>
                )}
                {!loading && dedupedItems.length === 0 && (
                    <Stack alignItems="center" sx={{ p: 3 }} gap={0.5}>
                        <Typography level="body-sm" color="neutral">
                            No upcoming meetings found.
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            Connect a calendar and ensure a sales rep has an external attendee on a future meeting.
                        </Typography>
                    </Stack>
                )}
            </Sheet>
        </Stack>
    )
}
